import { useMemo, type JSX } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';

import { Segmentation } from '@core';
import { useGraphData } from '@hooks';
import { useTranslate } from '@i18n';
import { useUiGraph } from '@queries';
import {
    activeAnalysisAtom,
    activeUserSettingsAtom,
    activityUnitAtom,
    comparisonAnalysisAtom,
    currentFrameAtom,
    currentPositionNumberAtom,
    selectedSwingGuideParameterAtom,
    swingGuideParametersByTimeSeriesIdAtom,
} from '@store';
import { Graph as GraphUI, Spinner } from '@ui';

import { ModuleBase } from '../ModuleBase';

export type GraphProps = {
    isEditing?: boolean;
    nodeId: string;
    options?: JSX.Element | null;
};

export const Graph = ({ isEditing, nodeId, options }: GraphProps) => {
    const userSettings = useAtomValue(activeUserSettingsAtom);
    const activityUnit = useAtomValue(activityUnitAtom);
    const currentFrame = useAtomValue(currentFrameAtom);
    const activeAnalysis = useAtomValue(activeAnalysisAtom);
    const selectedAnalysis = activeAnalysis?.fullAnalysis ?? activeAnalysis?.quickAnalysis;
    const comparison = useAtomValue(comparisonAnalysisAtom);
    const comparisonAnalysis = comparison?.fullAnalysis ?? comparison?.quickAnalysis;
    const activePosition = useAtomValue(currentPositionNumberAtom);
    const guideParameters = useAtomValue(swingGuideParametersByTimeSeriesIdAtom);

    const { data: modules } = useUiGraph();
    const setSelectedGuideParameterId = useSetAtom(selectedSwingGuideParameterAtom);

    const graphNode = useMemo(() => modules?.find((node) => node.id === nodeId), [modules, nodeId]);

    const nodeGraphType = graphNode?.categories.graphType;

    const { graphData, segmentation, graphLines } = useGraphData({
        nodeId,
        userPreferredUnit: activityUnit,
        selectedAnalysis,
        comparisonAnalysis,
    });
    const isLoading = !graphData || !graphLines;

    const t = useTranslate();
    const nodeName = t(graphNode?.i18n ?? '-');
    return (
        <ModuleBase
            title={nodeName}
            subtitle={t(nodeGraphType ? `${nodeGraphType}` : 'graph_type.fallback')}
            isClosed={isEditing}
            options={options}
        >
            {!isLoading ? (
                <GraphUI
                    data={graphData}
                    lines={graphLines}
                    segmentation={segmentation as { current: Segmentation; comparison: Segmentation | null }}
                    currentFrame={currentFrame}
                    activePosition={activePosition}
                    showComparison={userSettings.showComparison}
                    guideParameters={guideParameters}
                    onClickGuide={setSelectedGuideParameterId}
                />
            ) : (
                <Spinner size="l" />
            )}
        </ModuleBase>
    );
};
